import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import type {TestimonialsSectionSliceCarousel} from 'prismicio-types'
import {useState} from 'react'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {Navigation} from 'slices/Carousel/components/Navigation'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {useProgressBarRef} from 'slices/Carousel/useProgressBarRef'
import {Container} from 'src/common/Container'
import type {Swiper as SwiperClass} from 'swiper'
import {Autoplay, Pagination} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'

type CarouselVariationProps = {
  slice: TestimonialsSectionSliceCarousel & {
    slice_type: string
    variation: string
  }
}

export const TestimonialsCarouselVariation = ({slice}: CarouselVariationProps) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const {testimonials} = slice.primary
  const {progressBarRef, updateProgress} = useProgressBarRef()

  return (
    <Container
      className="!py-10 px-5 md:px-10 md:!pb-[54px] md:!pt-0 lg:px-5 xl:px-0"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <div
        className="grid-base relative max-w-[1200px] gap-y-10 lg:gap-y-[54px]"
        data-aos="fade-up"
        data-aos-offset="0"
      >
        <Swiper
          className="col-span-4 max-w-full md:col-span-12 md:w-full"
          spaceBetween={24}
          slidesPerView="auto"
          onSwiper={(swiper) => setSwiperInstance(swiper)}
          wrapperClass="ease-in-out duration-100"
          onActiveIndexChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          modules={[Pagination, Autoplay]}
          onAutoplayTimeLeft={(_, __, progress) => {
            updateProgress(progress)
          }}
          autoplay={{
            delay: (slice.primary.duration ?? 8) * 1000,
          }}
        >
          {testimonials.map((testimonial, index) => (
            <SwiperSlide
              className={cn('w-full max-w-[1200px] rounded-xl', {
                'bg-beige-200': true,
                'bg-moss-white': false,
              })}
              key={index}
            >
              <div className="grid size-full min-w-full grid-cols-[80px_auto] grid-rows-[auto_80px] gap-x-4 gap-y-6 rounded-lg bg-green-dark p-6 lg:max-w-[1176px] lg:grid-cols-2 lg:grid-rows-[auto_auto] lg:p-10 xl:gap-x-16">
                <div className="col-span-2 md:max-w-[500px] lg:col-span-1 lg:pr-8">
                  <PrismicRichText
                    field={testimonial.quote}
                    components={{
                      paragraph: ({children}) => (
                        <p className="text-tg-h3 text-moss-white md:leading-[normal] lg:text-32 lg:tracking-[-.32px]">
                          {children}
                        </p>
                      ),
                    }}
                  />
                </div>
                <PrismicNextImage
                  field={testimonial.image}
                  className="col-span-1 size-20 max-h-[80px] max-w-[80px] rounded-[4px] max-lg:self-end max-lg:object-cover lg:col-start-2 lg:row-span-2 lg:h-auto lg:max-h-none lg:w-full lg:max-w-[529px]"
                />
                <div className="col-span-1 col-start-2 row-start-2 flex flex-col max-lg:self-end lg:col-start-1 lg:justify-end">
                  <PrismicRichText
                    field={testimonial.author}
                    components={{
                      paragraph: ({children}) => (
                        <p className="text-button-s uppercase leading-[14px] text-moss-white">{children}</p>
                      ),
                      strong: ({children}) => (
                        <span className="text-button-s uppercase leading-[14px] text-green-neon">{children}</span>
                      ),
                    }}
                  />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="col-span-full mx-auto flex w-min">
          <Navigation
            isDarkMode={false}
            swiperInstance={swiperInstance}
            activeIndex={activeIndex}
            elements={testimonials.length}
            progressBarRef={progressBarRef}
          />
        </div>
      </div>
    </Container>
  )
}
